// app/(routes)/contact/page.tsx
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2,Clock, Shield } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { Suspense,useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { HTFButton } from '@/app/shared/components/button/HTFButtonComponent';
import { HFInputComponent } from '@/app/shared/components/forms/HFInputComponent';
import { HTFSection } from '@/app/shared/components/layout/HTFSection';
import { HTFHeading } from '@/app/shared/components/typography/HTFHeading';
import { SiteFooter } from '@/components/site-footer';
import { SiteHeader } from '@/components/site-header';

// Schema for validation
const contactFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  companyName: z.string().optional(),
  phoneNumber: z.string().optional(),
  message: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

function ContactPageContent() {
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [referralSource, setReferralSource] = useState<string>('direct');

  // Capture referral source from URL params
  useEffect(() => {
    const source = searchParams.get('source') || 
                   searchParams.get('utm_source') || 
                   searchParams.get('ref') || 
                   'direct';
    setReferralSource(source);
  }, [searchParams]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      phoneNumber: '',
      message: '',
    },
  });

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
  },
        body: JSON.stringify({
          ...data,
          referralSource,
          utmParams: {
            source: searchParams.get('utm_source'),
            medium: searchParams.get('utm_medium'),
            campaign: searchParams.get('utm_campaign'),
            term: searchParams.get('utm_term'),
            content: searchParams.get('utm_content'),
  },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit form');
      }

      setIsSubmitted(true);
      setShowCalendar(true);
      reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again or email us directly at hello@howthef.ai');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-beige text-black">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero Section */}
        <HTFSection bgColorVariant="beige" paddingY="xl" id="hero" maxWidth="8xl">
          <div className="max-w-4xl mx-auto text-center">
            <HTFHeading as="h1" size="5xl" fontWeight="extrabold" className="text-black mb-6">
              Working Automation in 90 Days or We Keep Working Free.
            </HTFHeading>
            <p className="text-2xl text-text-secondary mb-8">
              30-minute discovery call. We&apos;ll show you exactly what we&apos;d build. No deck. No BS. If we&apos;re not the right fit, we&apos;ll tell you.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-text-secondary">
              <div className="flex items-center gap-2">
                <Clock size={20} />
                <span className="text-lg">30 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={20} />
                <span className="text-lg">Zero obligation</span>
              </div>
            </div>
          </div>
        </HTFSection>

        {/* Form or Calendar Section */}
        <HTFSection bgColorVariant="surfaceLight" paddingY="lg" id="form" maxWidth="8xl">
          <div className="max-w-4xl mx-auto">
            {!showCalendar ? (
              <>
                {/* Contact Form */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-stone-200 p-8 md:p-12">
                  {referralSource && referralSource !== 'direct' && (
                    <div className="mb-4 text-sm text-text-secondary">
                      <span className="inline-block px-3 py-1 bg-teal/10 text-teal-dark rounded-md">
                        {referralSource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  )}
                  <HTFHeading as="h2" size="3xl" fontWeight="bold" className="text-black mb-2">
                    Tell Us About Your Project
                  </HTFHeading>
                  <p className="text-text-secondary mb-8">
                    Fill out the form below and we&apos;ll get back to you within 24 hours.
                  </p>

                  {isSubmitted && !showCalendar && (
                    <div className="mb-6 p-4 bg-teal/10 border border-teal rounded-lg flex items-start gap-3">
                      <CheckCircle2 className="text-teal flex-shrink-0 mt-0.5" size={20} />
                      <p className="text-teal-dark">
                        Thank you! Your information has been submitted. You can now schedule a call below.
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <HFInputComponent
                        control={control}
                        name="firstName"
                        label="First Name"
                        placeholder="John"
                        disabled={isSubmitting}
                        inputClassName="bg-white border-gray-300 focus:border-teal focus:ring-teal"
                      />

                      <HFInputComponent
                        control={control}
                        name="lastName"
                        label="Last Name"
                        placeholder="Doe"
                        disabled={isSubmitting}
                        inputClassName="bg-white border-gray-300 focus:border-teal focus:ring-teal"
                      />
                    </div>

                    <HFInputComponent
                      control={control}
                      name="email"
                      label="Work Email"
                      type="email"
                      placeholder="john@company.com"
                      disabled={isSubmitting}
                      inputClassName="bg-white border-gray-300 focus:border-teal focus:ring-teal"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <HFInputComponent
                        control={control}
                        name="companyName"
                        label="Company Name (Optional)"
                        placeholder="Acme Corp"
                        disabled={isSubmitting}
                        inputClassName="bg-white border-gray-300 focus:border-teal focus:ring-teal"
                      />

                      <HFInputComponent
                        control={control}
                        name="phoneNumber"
                        label="Phone Number (Optional)"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        disabled={isSubmitting}
                        inputClassName="bg-white border-gray-300 focus:border-teal focus:ring-teal"
                      />
                    </div>

                    <Controller
                      name="message"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-1.5">
                          <label htmlFor="message" className="text-gray-800 font-medium text-sm">
                            Tell us about your project (Optional)
                          </label>
                          <textarea
                            {...field}
                            id="message"
                            rows={4}
                            placeholder="What challenges are you facing? What are you looking to achieve?"
                            disabled={isSubmitting}
                            className="w-full px-3 py-2 text-base rounded-md shadow-sm bg-white text-gray-900 border border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:border-transparent hover:border-teal disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        </div>
                      )}
                    />

                    <div className="pt-4">
                      <HTFButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        disabled={!isValid || isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : 'Continue to Schedule Call'}
                      </HTFButton>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <>
                {/* Google Calendar Scheduler */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-stone-200">
                  <div className="p-8">
                    <div className="text-center mb-6">
                      <CheckCircle2 className="w-12 h-12 text-teal mx-auto mb-4" />
                      <HTFHeading as="h2" size="2xl" fontWeight="bold" className="text-black mb-2">
                        Thank You!
                      </HTFHeading>
                      <p className="text-lg text-text-secondary">
                        Now choose a time for your discovery call
                      </p>
                    </div>
                    
                    {/* Google Calendar Appointment Scheduling */}
                    <div className="rounded-xl overflow-hidden" style={{ minHeight: '600px' }}>
                      <iframe 
                        src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ3OA37FBRIy36XWkGVDmYLKWXyRl9YnuoiDBAkXXdTQri1KU6zPAau-KMgDacVr6WPOG9FmXCk9?gv=true" 
                        style={{ border: 0 }} 
                        width="100%" 
                        height="600" 
                        frameBorder="0"
                        title="Schedule a Discovery Call"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Alternative Contact */}
            <div className="mt-12 text-center">
              <p className="text-text-secondary mb-3">
                Prefer email? Reach us directly:
              </p>
              <a 
                href="mailto:hello@howthef.ai?subject=AI Implementation Inquiry" 
                className="text-teal hover:underline font-semibold text-xl"
              >
                hello@howthef.ai
              </a>
            </div>
          </div>
        </HTFSection>

        {/* Trust Section */}
        <HTFSection bgColorVariant="black" paddingY="lg" id="trust" maxWidth="8xl">
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <HTFHeading as="h2" size="3xl" fontWeight="extrabold" className="text-white">
              No Consultants. No Vendor Lock-In. No Bullshit.
            </HTFHeading>
            <p className="text-xl text-white/90">
              We build bespoke AI systems on FluxOS, our pre-built infrastructure that gets you working automation 3x faster than internal builds. Your team owns the result. We train them to maintain it.
              <br /><br />
              If we can&apos;t deliver working automation in 90 days, we keep working free until it&apos;s done. That&apos;s the guarantee.
            </p>
          </div>
        </HTFSection>
      </main>
      <SiteFooter />
    </div>
  );
}

export default function ContactPage() {
  return (
    <Suspense fallback={
      <div className="flex min-h-screen flex-col bg-beige text-black">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-text-secondary">Loading...</p>
          </div>
        </main>
        <SiteFooter />
      </div>
    }>
      <ContactPageContent />
    </Suspense>
  );
}
